import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../config/api';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail) {
      setIsError(true);
      setMessage('Please enter your email address.');
      return;
    }

    setIsSubmitting(true);
    setMessage('');

    try {
      const response = await apiClient.post('/auth/reset-password', { email: trimmedEmail });
      const msg = response?.data?.message
        || 'If an account exists for that email, a reset link has been sent.';
      setIsError(false);
      setMessage(msg);
    } catch (error) {
      const apiMsg = error?.response?.data?.error;
      setIsError(true);
      setMessage(apiMsg || 'Unable to process your request right now. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main style={styles.wrapper}>
      <section style={styles.card} aria-labelledby="forgot-password-title">
        <h1 id="forgot-password-title" style={styles.heading}>Forgot your password?</h1>
        <p style={styles.helper}>
          {/* Enter the email address associated with your account and we&apos;ll send you a link to reset your password. */}
          Please enter the email address linked to your account. You&apos;ll receive a password reset email shortly.
        </p>

        <form onSubmit={handleSubmit} style={styles.form}>
          <label htmlFor="forgot-email" style={styles.label}>Email address</label>
          <input
            id="forgot-email"
            type="email"
            name="email"
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="name@company.com"
            required
            style={styles.input}
          />

          <button
            type="submit"
            style={{
              ...styles.button,
              ...(isSubmitting ? styles.buttonDisabled : {}),
            }}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Sending...' : 'Send reset link'}
          </button>
        </form>

        {message ? (
          <p
            role="status"
            style={{
              ...styles.message,
              color: isError ? '#b42318' : '#027a48',
            }}
          >
            {message}
          </p>
        ) : null}

        <div style={styles.links}>
          <Link to="/login" style={styles.link}>Back to login</Link>
          <span style={styles.separator}>|</span>
          <Link to="/register" style={styles.link}>Create account</Link>
        </div>
      </section>
    </main>
  );
};

const styles = {
  wrapper: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #0f162b, #1f3c88)',
  },
  card: {
    width: '100%',
    maxWidth: '420px',
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '32px',
    boxShadow: '0 18px 36px rgba(15, 23, 42, 0.15)',
  },
  heading: {
    margin: '0 0 12px',
    fontSize: '28px',
    fontWeight: 700,
    color: '#1f3c88',
  },
  helper: {
    margin: '0 0 24px',
    color: '#475467',
    lineHeight: 1.5,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  label: {
    fontWeight: 600,
    color: '#1d2939',
  },
  input: {
    padding: '12px 14px',
    borderRadius: '10px',
    border: '1px solid #d0d5dd',
    fontSize: '16px',
  },
  button: {
    marginTop: '12px',
    background: 'linear-gradient(135deg, #1f3c88, #39a0ed)',
    color: '#fff',
    border: 'none',
    borderRadius: '999px',
    padding: '14px',
    fontSize: '16px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    boxShadow: '0 14px 24px rgba(31, 60, 136, 0.3)',
  },
  buttonDisabled: {
    opacity: 0.75,
    cursor: 'not-allowed',
    transform: 'none',
    boxShadow: 'none',
  },
  message: {
    marginTop: '20px',
    fontSize: '15px',
    lineHeight: 1.5,
  },
  links: {
    marginTop: '32px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    color: '#5f6c7b',
  },
  link: {
    color: '#1f3c88',
    fontWeight: 600,
    textDecoration: 'none',
  },
  separator: {
    color: '#d0d5dd',
  },
};

export default ForgotPassword;
